import cx from 'classix';
import { createContext, ReactNode, useContext, useRef } from 'react';
import {
  AriaRadioGroupProps,
  AriaRadioProps,
  mergeProps,
  useFocusRing,
  useRadio,
  useRadioGroup,
} from 'react-aria';
import { UseFormRegisterReturn } from 'react-hook-form';
import { RadioGroupState, useRadioGroupState } from 'react-stately';
import { PillBadge, PillProps } from './Badge';
import { SelectBox } from './Checkbox';

export type RadioItem = {
  value: string;
  title: string | ReactNode;
  description?: string | ReactNode;
  pillContent?: string | ReactNode;
};

// TODO validation likely needs re-visit with forms, currently used straight from docs 'validation behavior'
// TODO revisit accessibility - mocks do not include label, commented out for now

type RadioContextType = {
  state: RadioGroupState;
};
const RadioContext = createContext<RadioContextType | null>(null);

const Radio = (
  props: AriaRadioProps & {
    // TODO: Remove/refactor depending on feedback about borderless radios
    alignStart?: boolean;
    // isLastElement: boolean;
    register?: () => UseFormRegisterReturn<string>;
  },
) => {
  const { register } = props;
  const ref = useRef<HTMLInputElement>(null);
  const { isFocusVisible, focusProps } = useFocusRing();
  const context = useContext(RadioContext);
  // This seems odd but otherwise hit type error on possible null 'state' value for useRadio()
  if (!context) {
    throw new Error('Radio component used outside of Radio Group');
  }
  const { state } = context;
  const { inputProps } = useRadio(props, state, ref);
  const isSelected = state.selectedValue === props.value;
  const isDisabled = state.isDisabled;

  return (
    // It's odd for this outermost element of an individual radio to be a label and likely an issue for a11y
    // However, it's load-bearing in that switching to a div makes the radio buttons unclickable!
    <label
      className={cx(
        'flex gap-3 min-h-[72px] cursor-pointer',
        props.alignStart ? 'items-start p-4' : 'items-center p-6',
      )}
    >
      <div className='hidden'>
        {register && <input {...register()} {...mergeProps(inputProps, focusProps)} ref={ref} />}
        {!register && <input {...mergeProps(inputProps, focusProps)} ref={ref} />}
      </div>
      <SelectBox
        type='dot'
        isDisabled={isDisabled}
        isSelected={isSelected}
        isFocusVisible={isFocusVisible}
        size='md'
      />
      <div className='text-md'>{props.children}</div>
    </label>
  );
};

const RadioGroupBase = (
  props: AriaRadioGroupProps & {
    children: ReactNode;
    onChange: () => void;
    // TODO: Short-term hack to achieve the non-bordered radio group style used in the status modal,
    // even though the DS doesn't describe this. Need to check what we actually want here with Julissa
    borderless?: boolean;
  },
) => {
  const {
    children,
    onChange,
    // label,
    // description
  } = props;
  const state = useRadioGroupState({
    ...props,
    defaultValue: props.defaultValue,
  });
  const {
    radioGroupProps,
    errorMessageProps,
    isInvalid,
    validationErrors,
    // labelProps,
    // descriptionProps,
  } = useRadioGroup(props, state);
  return (
    <>
      <div
        {...radioGroupProps}
        className={cx(
          !props.borderless &&
            'border border-gray-100 rounded-xl divide-y divide-solid divide-gray-100',
        )}
        onChange={onChange}
      >
        {/* <span {...labelProps}>{label}</span> */}
        <RadioContext.Provider value={{ state }}>{children}</RadioContext.Provider>
        {/* {description && (
        <div {...descriptionProps} style={{ fontSize: 12 }}>
          {description}
        </div>
      )} */}
        {/* TODO this is  placeholder validation JSX straight from docs */}
      </div>
      {isInvalid && (
        <div {...errorMessageProps} className='text-sm text-error-500 p-1 pt-4'>
          {validationErrors.join(' ')}
        </div>
      )}
    </>
  );
};

export const RadioGroup = (props: {
  items: RadioItem[];
  name: string;
  ['aria-label']: string;
  defaultValue?: RadioItem['value'];
  onChange: (value?: string | number) => void;
  isRequired?: boolean;
  isDisabled?: boolean;
  validate?: (value: string | number | null) => string | undefined;
  // label?: string;
  // description?: string;
  // TODO: Short term hack (see usage above); remove
  borderless?: boolean;
  alignStart?: boolean;
  register?: () => UseFormRegisterReturn<string>;
  error?: string;
  pillProps?: PillProps;
}) => {
  return (
    <div>
      <RadioGroupBase
        name={props.name}
        validate={props.validate}
        aria-label={props['aria-label']}
        isDisabled={props.isDisabled}
        onChange={props.onChange}
        validationBehavior='native'
        isRequired={props.isRequired}
        borderless={props.borderless}
        defaultValue={props.defaultValue}
      >
        {props.items.map((item, i) => (
          <Radio
            key={`${props.name}-radio-item-${i}`}
            value={item.value.toString()}
            alignStart={props.borderless || props.alignStart}
            register={props.register}
          >
            <div className='flex flex-col gap-1'>
              {/* TODO: Debug this negative margin hack for borderless */}
              <div
                className={cx(
                  props.borderless && 'mt-[-2px]',
                  'text-gray-700 text-md font-medium flex flex-row gap-2',
                )}
              >
                {item.title}
                {item.pillContent && props.pillProps && (
                  <PillBadge {...props.pillProps}>{item.pillContent}</PillBadge>
                )}
              </div>
              {item.description && (
                <span className='text-gray-500 text-sm'>{item.description}</span>
              )}
            </div>
          </Radio>
        ))}
      </RadioGroupBase>
      {props.error && <div className='text-sm text-error-500 p-1'>{props.error}</div>}
    </div>
  );
};
